import type { FieldIssue } from '@/composables/useFieldValidation'

/**
 * The value-only rules the portal's forms already enforce (a `required` field, a `maxlength`, a `min`, a
 * parse), in the words a field shows while the user types (#1757). Each says what is wrong and how to fix
 * it, with no code. None is new: the API still enforces its own copy.
 */

/** A required name: empty, or only spaces, is refused. `what` is the thing being named ("team", "role"…). */
export function requiredName(what: string): (value: unknown) => FieldIssue | null {
    return required('Name is empty.', `Give the ${what} a name.`)
}

/** A required value of any kind: empty, or only spaces, is refused. */
export function required(message: string, fix: string): (value: unknown) => FieldIssue | null {
    return value => (String(value ?? '').trim() === '' ? { message, fix } : null)
}

/** A text field with a `maxlength`: longer text is refused, naming the limit. */
export function maxLength(label: string, limit: number): (value: unknown) => FieldIssue | null {
    return value =>
        String(value ?? '').length > limit
            ? {
                  message: `${label} is longer than the ${limit} characters it allows.`,
                  fix: `Shorten it to ${limit} characters or fewer.`,
              }
            : null
}

/** An amount that may be left blank, but otherwise is a number of 0 or more (a field with `min="0"`). */
export function optionalAmount(label: string, example = '2.50'): (value: unknown) => FieldIssue | null {
    return value => {
        const text = String(value ?? '').trim()
        if (text === '') return null
        const amount = Number(text)
        return Number.isFinite(amount) && amount >= 0
            ? null
            : {
                  message: `${label} is not a number of 0 or more.`,
                  fix: `Enter 0 or more, like ${example}, or leave it empty.`,
              }
    }
}

/** A slug the server refused because another row already has it. */
export const SLUG_TAKEN: FieldIssue = {
    message: 'That slug is already taken.',
    fix: 'Choose a different slug, or leave it empty to make one from the name.',
}
